import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebaseConfig";
import "./ActivityLog.css"; // Ensure this CSS file exists for styling
// Display appointment dates in Michigan time
const formatAppointmentDate = (value) => {
  if (!value || value === "Not Set") return "Not Set";

  const date = DateTime.fromISO(value, { zone: "utc" })
    .setZone("America/Detroit");

  return date.isValid
    ? date.toFormat("MM/dd/yyyy")
    : value;
};

// Display appointment start/end times in Michigan time
const formatAppointmentTime = (value) => {
  if (!value) return "";

  const date = DateTime.fromISO(value, { zone: "utc" })
    .setZone("America/Detroit");

  return date.isValid
    ? date.toFormat("MM/dd/yyyy, h:mm a")
    : value;
};

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterTerm, setFilterTerm] = useState(""); // New state for client name or appointment date search

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const logsRef = collection(db, "activity_logs");
        const q = query(logsRef, orderBy("timestamp", "desc"));
        const querySnapshot = await getDocs(q);

        const logData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp
            ? doc.data().timestamp
                .toDate()
                .toLocaleString("en-US", { timeZone: "America/New_York" })
            : ""
        }));

        setLogs(logData);
        setLoading(false);
      } catch (error) {
        console.error("❌ Error fetching activity logs:", error);
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  // Filter logs based on user, action, and filterTerm (searching clientName and appointmentDate)
  const filteredLogs = logs.filter((log) => {
    const term = filterTerm.toLowerCase();
    const clientMatch =
      log.clientName && log.clientName.toLowerCase().includes(term);
    const dateMatch =
      log.appointmentDate &&
      formatAppointmentDate(log.appointmentDate).includes(term);
    return (
      (filterUser === "" || log.user.toLowerCase().includes(filterUser.toLowerCase())) &&
      (filterAction === "" || log.action === filterAction) &&
      (filterTerm === "" || clientMatch || dateMatch)
    );
  });

  return (
    <div className="activity-log-container">
      <h2>📜 Activity Log</h2>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by User"
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        />
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
        >
          <option value="">All Actions</option>
<option value="Created">Created</option>
<option value="Created Appointment">Created Appointment</option>
<option value="Copied Appointment">Copied Appointment</option>
<option value="Updated Appointment">Updated Appointment</option>
<option value="Moved Appointment">Moved Appointment</option>
<option value="Deleted">Deleted</option>
<option value="Restored">Restored</option>
        </select>
        <input
          type="text"
          placeholder="Search by Client or Date (MM/DD/YYYY)"
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
        />
        <button
          onClick={() => {
            setFilterUser("");
            setFilterAction("");
            setFilterTerm("");
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Loading Indicator */}
      {loading ? <p>Loading logs...</p> : null}

      {/* Log List */}
      <ul className="log-list">
        {filteredLogs.length > 0 ? (
          filteredLogs.map((log) => (
            <li key={log.id} className={`log-item ${log.action.toLowerCase()}`}>
              <p>
                <strong>User:</strong> {log.user}
              </p>
              <p>
                <strong>Action:</strong> {log.action}
              </p>
              <p>
                <strong>Timestamp:</strong> {log.timestamp}
              </p>
              <p>
                <strong>Client:</strong> {log.clientName || "Unknown Client"}
                <br />
                <small>
                  <strong>Appointment Date:</strong>{" "}
                {formatAppointmentDate(log.appointmentDate)}
                </small>
              </p>
              {log.changedFields && (
                <details>
                  <summary>View Changes</summary>
                  <ul>
                    {Object.entries(log.changedFields).map(([key, value]) => (
                      <li key={key}>
                        <strong>{key}:</strong>{" "}
                        {["startTime", "endTime", "appointmentDate", "endDate", "originalDate", "newDate", "deletedDate"].includes(key)
  ? String(value).split(" → ").map((part) => {
      const trimmed = part.trim();

      return ["appointmentDate", "originalDate", "newDate", "deletedDate"].includes(key)
        ? formatAppointmentDate(trimmed)
        : formatAppointmentTime(trimmed);
    }).join(" → ")
  : typeof value === "object"
    ? JSON.stringify(value)
    : value}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          ))
        ) : (
          <p>No logs found.</p>
        )}
      </ul>
    </div>
  );
};

export default ActivityLog;
